import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client";
import {
  START_AI_COPILOT_CONVERSATION,
  CHAT_WITH_AI_COPILOT,
  AI_COPILOT_HEALTH_CHECK,
} from "../../graphql/AICopilotQueries";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  recipeContext?: string;
}

interface AICopilotChatProps {
  selectedRecipe?: string;
  onRecipeModified?: (recipeName: string, content: string) => void;
}

const AICopilotChat: React.FC<AICopilotChatProps> = ({
  selectedRecipe,
  onRecipeModified,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // GraphQL hooks
  const { data: healthData, error: healthError } = useQuery(
    AI_COPILOT_HEALTH_CHECK,
  );

  const [startConversation, { loading: startingConversation }] = useMutation(
    START_AI_COPILOT_CONVERSATION,
    {
      onCompleted: (data: {
        startAiCopilotConversation: {
          conversationContext: {
            conversationId: string;
            messages: Message[];
          };
        };
      }) => {
        const context = data.startAiCopilotConversation.conversationContext;
        setConversationId(context.conversationId);
        setMessages(context.messages || []);
        setError(null);

        // Add welcome message if no messages exist
        if (!context.messages || context.messages.length === 0) {
          const welcomeMessage: Message = {
            role: "assistant",
            content:
              "Hello! I'm your AI assistant for radiotherapy blueprint editing. What would you like to know or change?",
            timestamp: new Date().toISOString(),
            recipeContext: selectedRecipe,
          };
          setMessages([welcomeMessage]);
        }
      },
      onError: (error: Error) => {
        setError(error.message);
      },
    },
  );

  const [chatWithCopilot, { loading: chatLoading }] = useMutation(
    CHAT_WITH_AI_COPILOT,
    {
      onCompleted: (data: {
        chatWithAiCopilot: {
          chatResponse: {
            response: string;
            recipeModified: boolean;
            recipeContent?: string;
            conversationId: string;
          };
        };
      }) => {
        const response = data.chatWithAiCopilot.chatResponse;

        const assistantMessage: Message = {
          role: "assistant",
          content: response.response,
          timestamp: new Date().toISOString(),
          recipeContext: selectedRecipe,
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // If recipe was modified, notify parent component
        if (
          response.recipeModified &&
          response.recipeContent &&
          onRecipeModified
        ) {
          onRecipeModified(selectedRecipe || "", response.recipeContent);
        }

        // Update conversation ID if it changed
        if (
          response.conversationId &&
          response.conversationId !== conversationId
        ) {
          setConversationId(response.conversationId);
        }

        setError(null);
      },
      onError: (error: Error) => {
        setError(error.message);
      },
    },
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Start a new conversation when recipe changes
    if (selectedRecipe) {
      startNewConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecipe]);

  const startNewConversation = async () => {
    try {
      setError(null);
      await startConversation({
        variables: {
          recipeName: selectedRecipe,
        },
      });
    } catch (err) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const sendMessageWithContent = (content: string) => {
    if (!content.trim() || startingConversation || chatLoading || !!healthError)
      return;

    const newMessage: Message = {
      role: "user",
      content: content,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputMessage("");

    if (conversationId) {
      chatWithCopilot({
        variables: {
          conversationId,
          message: content,
          recipeName: selectedRecipe || "",
        },
      });
    } else {
      startConversation({
        variables: {
          message: content,
          recipeName: selectedRecipe || "",
        },
      });
    }
  };

  const sendMessage = () => {
    sendMessageWithContent(inputMessage);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputMessage(suggestion);
    // Auto-send the suggestion
    sendMessageWithContent(suggestion);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const isLoading = startingConversation || chatLoading;

  const MessageBubble: React.FC<{ message: Message }> = ({ message }) => (
    <ListItem
      sx={{
        display: "flex",
        flexDirection: message.role === "user" ? "row-reverse" : "row",
        alignItems: "flex-start",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: message.role === "user" ? "flex-end" : "flex-start",
          maxWidth: "70%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.5,
          }}
        >
          {message.role === "user" ? (
            <PersonIcon fontSize="small" color="primary" />
          ) : (
            <BotIcon fontSize="small" color="secondary" />
          )}
          <Typography variant="caption" color="text.secondary">
            {message.role === "user" ? "You" : "AI assistant"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(message.timestamp)}
          </Typography>
        </Box>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            backgroundColor:
              message.role === "user" ? "primary.main" : "background.default",
            color:
              message.role === "user" ? "primary.contrastText" : "text.primary",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {message.content}
          </Typography>
        </Paper>
        {message.recipeContext && (
          <Chip
            label={`Recipe: ${message.recipeContext}`}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        )}
      </Box>
    </ListItem>
  );

  return (
    <Stack
      sx={{
        height: "100%",
        display: "flex",
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 2 }}
      >
        {/* Health status indicator */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h6">AI assistant</Typography>
          {healthError && (
            <Chip
              label="Service Unavailable"
              color="error"
              variant="outlined"
              size="small"
              sx={{ mt: 1, ml: 1 }}
            />
          )}
          {healthData?.aiCopilotHealth?.status === "healthy" && (
            <Chip
              label="Service Online"
              color="success"
              variant="outlined"
              size="small"
              sx={{ mt: 1, ml: 1 }}
            />
          )}
        </Stack>
        <Tooltip title="Start new conversation">
          <IconButton
            onClick={() => {
              startNewConversation();
            }}
            disabled={isLoading}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Health Error Display */}
      {healthError && (
        <Alert severity="warning" sx={{ m: 2 }}>
          AI assistant is currently unavailable. Please try again later.
        </Alert>
      )}

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
        <List sx={{ maxHeight: "200px" }}>
          {messages.map((message, index) => (
            <MessageBubble key={index} message={message} />
          ))}
          {isLoading && (
            <ListItem sx={{ justifyContent: "center" }}>
              <CircularProgress size={24} />
            </ListItem>
          )}
          <div ref={messagesEndRef} />
        </List>
      </Box>

      <Divider />

      {/* Suggestions */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ p: 2, pb: 0 }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block" }}
        >
          Suggestions for this blueprint:
        </Typography>
        <Chip
          label="Reduce the maximum dose to the Rectum"
          variant="outlined"
          size="small"
          onClick={() =>
            handleSuggestionClick("Reduce the maximum dose to the Rectum")
          }
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "primary.light",
            },
          }}
          disabled={isLoading || !!healthError}
        />
      </Stack>

      {/* Input */}
      <Box sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Ask me about the recipe or request changes..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading || !!healthError}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={isLoading || !inputMessage.trim() || !!healthError}
            sx={{ minWidth: "auto", px: 2 }}
          >
            <SendIcon />
          </Button>
        </Box>
      </Box>
    </Stack>
  );
};

export default AICopilotChat;
